import type { UIMessage } from 'ai'
import { isTextUIPart, isToolUIPart } from 'ai'
import type {
  LlmChatMessageFeedbackDto,
  SubmitLlmChatMessageFeedbackPayload,
} from '@/shared/hooks/api/llmChat'
import { LlmChatAssistantMessageFeedback } from './LlmChatAssistantMessageFeedback'
import { LlmChatAssistantPendingStatus } from './LlmChatAssistantPendingStatus'
import { LlmChatAssistantReasoningPart } from './LlmChatAssistantReasoningPart'
import { LlmChatAssistantStepDivider } from './LlmChatAssistantStepDivider'
import { LlmChatAssistantTextPart } from './LlmChatAssistantTextPart'
import { LlmChatAssistantToolCallBlock } from './LlmChatAssistantToolCallBlock'
import type { LegislationCitationTarget } from './llmChatToolOutput'
import { messageHasProductRecommendationTool } from '../llmChatFeedbackEligibility'
import { useLlmChatAssistantStreamState } from './useLlmChatAssistantStreamState'

export type LlmChatAssistantMessageBubbleProps = {
  message: UIMessage
  sessionId: string
  sessionProductId?: string | null
  /** This assistant row is the target of the in-flight chat request */
  isActiveAssistantTurn?: boolean
  /** Request is in flight but stream chunks have not started yet */
  isAwaitingAssistantFirstChunk?: boolean
  /**
   * Response body is streaming from the transport (tool/reasoning tokens may
   * arrive before any assistant text part exists).
   */
  isAssistantResponseStreaming?: boolean
  /** Final assistant turn before the next user message (or end of thread). */
  isLastAssistantMessageInRun?: boolean
  existingFeedback?: LlmChatMessageFeedbackDto
  isFeedbackSubmitting?: boolean
  onSubmitFeedback: (
    messageId: string,
    payload: SubmitLlmChatMessageFeedbackPayload
  ) => Promise<void>
  onOpenCitationLink: (input: {
    href: string
    label: string
  }) => boolean | Promise<boolean>
  onOpenCitation: (target: LegislationCitationTarget) => void
  isChatStreamBusy?: boolean
  onContinueAfterCatalogSave?: (recommendationId: string) => void
  onContinueAfterCatalogDismiss?: (recommendationId: string) => void
}

export function LlmChatAssistantMessageBubble({
  message,
  sessionId,
  sessionProductId,
  isActiveAssistantTurn = false,
  isAwaitingAssistantFirstChunk = false,
  isAssistantResponseStreaming = false,
  isLastAssistantMessageInRun = false,
  existingFeedback,
  isFeedbackSubmitting = false,
  onSubmitFeedback,
  onOpenCitationLink,
  onOpenCitation,
  isChatStreamBusy,
  onContinueAfterCatalogSave,
  onContinueAfterCatalogDismiss,
}: LlmChatAssistantMessageBubbleProps) {
  const { lastTextPartIndex, showTextCursor, hasAssistantTextParts } =
    useLlmChatAssistantStreamState(message, {
      isActiveAssistantTurn,
      isAwaitingAssistantFirstChunk,
      isAssistantResponseStreaming,
    })

  const showFeedbackControls =
    !isActiveAssistantTurn &&
    !isAwaitingAssistantFirstChunk &&
    !isAssistantResponseStreaming &&
    isLastAssistantMessageInRun &&
    !messageHasProductRecommendationTool(message)

  return (
    <div className='flex w-full justify-start'>
      <div className='w-full max-w-[min(100%,42rem)]'>
        <div className='flex flex-col gap-2 text-[13px] leading-6 text-foreground'>
          {message.parts.map((part, index) => {
            const key = `${message.id}-part-${index}`
            if (part.type === 'reasoning') {
              return (
                <LlmChatAssistantReasoningPart
                  key={key}
                  part={part}
                  isActiveAssistantTurn={isActiveAssistantTurn}
                  isAwaitingAssistantFirstChunk={isAwaitingAssistantFirstChunk}
                  isAssistantResponseStreaming={isAssistantResponseStreaming}
                  hasAssistantTextParts={hasAssistantTextParts}
                />
              )
            }
            if (part.type === 'step-start') {
              return <LlmChatAssistantStepDivider key={key} />
            }
            if (isTextUIPart(part)) {
              return (
                <LlmChatAssistantTextPart
                  key={key}
                  part={part}
                  showTextCursor={showTextCursor}
                  isLastTextPart={index === lastTextPartIndex}
                  onOpenCitationLink={onOpenCitationLink}
                />
              )
            }
            if (isToolUIPart(part)) {
              return (
                <LlmChatAssistantToolCallBlock
                  key={key}
                  part={part}
                  onOpenCitation={onOpenCitation}
                  sessionId={sessionId}
                  productId={sessionProductId}
                  isChatStreamBusy={isChatStreamBusy}
                  onContinueAfterCatalogSave={onContinueAfterCatalogSave}
                  onContinueAfterCatalogDismiss={onContinueAfterCatalogDismiss}
                />
              )
            }
            return null
          })}
          {showTextCursor && lastTextPartIndex < 0 ? (
            <LlmChatAssistantPendingStatus parts={message.parts} />
          ) : null}
          {showFeedbackControls ? (
            <LlmChatAssistantMessageFeedback
              sessionId={sessionId}
              messageId={message.id}
              feedback={existingFeedback}
              isSubmitting={isFeedbackSubmitting}
              onSubmitFeedback={onSubmitFeedback}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
